"use client";
/**
 * The face-up comparison card: 236 pixels of "which of these".
 *
 * Every card is the same height, and that is structural rather than remembered:
 * the shell's `auto 1fr auto` regions let the body absorb the difference
 * between a short card and a tall one, so no card leaves a void and no card
 * needs a floor.
 *
 * It stamps its OWN name. This composition is fixed — photograph, title, meta
 * line, energy figure, macros — and none of the three card selections changes
 * any of it, so borrowing the live selection's name would stamp `Macro board`
 * on a card carrying a 230-pixel photograph, and `Macro board` is the
 * composition declared to have no media at all.
 */
import type { ReactElement } from "react";
import {
  CresultcardIdentity,
  CresultcardOpenHint,
  CRC_TILE_INSET,
  CresultcardAlign,
  CresultcardDensity,
  CresultcardFigureScale,
  CresultcardIdentitySlot,
  CresultcardMediaScale,
  CresultcardTilePadding,
  CresultcardTitleLines,
  useCresultcardLightbox,
  useCresultcardVariant,
} from "@/components/ui/Cresultcard";
import {
  C_NUTRITION_RAIL_MODIFIER,
  C_NUTRITION_RAIL_VARIANT,
  C_NUTRITION_SERVING_SCOPES,
  C_NUTRITION_SERVING_SCOPE_GROUP,
} from "./cnutritioncard.constants";
import {
  CnutritioncardEnergy,
  CnutritioncardMacroStrip,
  CnutritioncardMetaLine,
  CnutritioncardShot,
} from "./CnutritioncardMeta";
import { CnutritioncardShell } from "./CnutritioncardShell";
import type { CnutritioncardRailProps } from "./cnutritioncard.types";

const CARD_INSET = CRC_TILE_INSET[CresultcardTilePadding.Snug];
const RAIL_TRUNCATION: readonly CresultcardIdentitySlot[] = [
  CresultcardIdentitySlot.Title,
];

export function CnutritioncardRail({
  data,
  servingScope,
  openControl,
}: CnutritioncardRailProps): ReactElement {
  const { value: registryScope } = useCresultcardVariant({
    group: C_NUTRITION_SERVING_SCOPE_GROUP,
    options: C_NUTRITION_SERVING_SCOPES,
  });
  const { open, viewer } = useCresultcardLightbox({ assets: data.images });

  return (
    <>
      <CnutritioncardShell
        density={CresultcardDensity.Rail}
        head={
          <CnutritioncardShot
            data={data}
            onOpen={() => open(0)}
            scale={CresultcardMediaScale.Face}
          />
        }
        modifier={C_NUTRITION_RAIL_MODIFIER}
        openable
        variant={C_NUTRITION_RAIL_VARIANT}
        body={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              minWidth: 0,
              padding: CARD_INSET,
            }}
          >
            <CresultcardIdentity
              openControl={openControl}
              title={data.name}
              titleLines={CresultcardTitleLines.Two}
              truncationOrder={RAIL_TRUNCATION}
            />
            <CnutritioncardMetaLine
              data={data}
            />
            {/* The macro split lives in the BODY, at the card's full 230px, so
                the kit's stat strip can wrap its three labelled cells onto a
                second line instead of the foot's narrow column ellipsising
                them. `Carbohydrate 13.15 g · Fat 5.91 g · Protein 3.06 g` is
                50 characters and does not fit one 150px line at any size. */}
            <CnutritioncardMacroStrip
              data={data}
            />
          </div>
        }
        foot={
          <div
            data-el="Cnutritioncard/Rail/Footer"
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: CARD_INSET,
              minWidth: 0,
              paddingBlock: CARD_INSET,
              paddingInline: CARD_INSET,
            }}
          >
            <CnutritioncardEnergy
              align={CresultcardAlign.Start}
              data={data}
              scale={CresultcardFigureScale.Rail}
              servingScope={servingScope ?? registryScope}
            />
            <CresultcardOpenHint />
          </div>
        }
      />
      {viewer}
    </>
  );
}
